import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import useSet from '@shared/utils/hooks/useSet';
import { useTimeout } from '@shared/hooks/useTimeout';
import globalWindow from '@shared/core/globals';
// Will fix the following 2 cross-app import violations in the upcoming joy-web structural refactor
import { layoutConfigs } from '@apps/guest/packages/layout-engine/layouts/layout.constants';
import { googleFontFamilies } from '@apps/guest/packages/layout-engine/common/fonts';
import type { Theme } from '@withjoy/joykit';
import type { EventDesignFragment } from '@graphql/fragments/eventDesign.graphql.generated';
import type { FontPreloadLinkAttributes } from './types.internal';
import { buildSelfHostedFontCssUrl, buildCriticalPreloadUrls, normalizeFontFamily } from './selfHostedFonts';
import { extractFontFamiliesFromHtml, getHeaderEventTitleHtml } from './extractFontFamiliesFromHtml';
import { addError } from '@shared/utils/logger';

/**
 * Font loading timing constants
 *
 * These values are tuned for optimal balance between performance and user experience:
 * - Too short: Fonts marked as loaded before browser starts downloading (more FOUT)
 * - Too long: Unnecessary delay in applying font-family CSS (slower perceived load)
 */

// Allow browser time to discover and start downloading preloaded fonts before marking as loaded.
// 100ms gives browser time to parse <link rel="preload"> tags and initiate font downloads.
// Combined with font-display:swap, this minimizes FOUT while keeping LCP fast.
const FONT_PRELOAD_DISCOVERY_DELAY_MS = 100;

// Brief delay after WebFont.js reports fonts painted to ensure fonts are fully rendered
// before updating theme. Prevents jitter in designer preview during font transitions.
const FONT_PAINT_DELAY_MS = 50;

// Fallback timeout when WebFont.js fails to load fonts (network issues, invalid font, etc.).
// Long enough to handle slow connections but short enough to not block UI indefinitely.
const FONT_LOAD_TIMEOUT_FALLBACK_MS = 1000;

// Default priority for fonts without an explicit category mapping.
// Load last (after all categorized fonts) to prioritize content-critical fonts.
const UNCATEGORIZED_FONT_PRIORITY = 99;

// Maximum number of fonts to accumulate before clearing the cache.
// Prevents unbounded memory growth in long designer sessions.
// When limit is reached, cache is cleared to free browser memory (will cause brief flicker).
const MAX_ACCUMULATED_FONTS = 20;

/**
 * Build Google Fonts CSS2 URL with proper weight/style support.
 *
 * Requests multiple weights (400/700) to support proper bold rendering.
 * - Wedding websites require proper bold weights (synthetic bold appears too light)
 * - Google Fonts API returns only available weights (e.g., Alex Brush returns only 400)
 * - No timeout issues when combined with proper font accumulation + newFonts detection
 *
 * font-display: swap allows text to render immediately with fallback fonts,
 * then swap to custom fonts once loaded. This prevents blocking render and improves LCP.
 */
export const buildGoogleFontUrl = (fontFamily: string, options?: { includeItalic?: boolean }): string => {
  const url = new URL('https://fonts.googleapis.com/css2');

  // Noto Serif needs weight 500 (medium) in addition to 400/700 for proper rendering
  const defaultWeights = fontFamily === 'Noto Serif' ? [400, 500, 700] : [400, 700];

  if (options?.includeItalic) {
    // Font pack fonts: include italic variants with multiple weights
    // Format: ital,wght@0,400;0,700;1,400;1,700 (normal 400, normal 700, italic 400, italic 700)
    const normalWeights = defaultWeights.map(w => `0,${w}`).join(';');
    const italicWeights = defaultWeights.map(w => `1,${w}`).join(';');
    url.searchParams.set('family', `${fontFamily}:ital,wght@${normalWeights};${italicWeights}`);
  } else {
    // Layout/primary fonts: normal weights only
    const weightsList = defaultWeights.join(';');
    url.searchParams.set('family', `${fontFamily}:wght@${weightsList}`);
  }

  url.searchParams.set('display', 'swap');

  return url.toString();
};

// Prioritize fonts for unauthenticated (pre-unlock) content to optimize initial LCP.
// Protected content (DATE, LOCATION, BUTTONS) loads dynamically after auth and is deprioritized.
// Lower number = higher priority (loads first)
const CATEGORY_PRIORITY_MAP: Record<string, number> = {
  // CRITICAL: Visible in initial SSR (pre-auth) - impacts LCP
  EVENT_TITLE: 1, // Hero title - LCP element, always public
  PAGE_TITLE: 2, // Top navigation - always visible
  NAVIGATION: 3, // Nav menu items - always visible
  SUB_HEADING: 4, // Subtitle under hero - visible before unlock

  // SECONDARY: Protected content - loads after auth/unlock (less critical for initial LCP)
  DATE: 5, // Password-protected - dynamic load after unlock
  LOCATION: 6, // Password-protected - dynamic load after unlock
  BUTTONS: 7, // CTAs - may change after unlock

  // TERTIARY: Below fold or less critical
  HEADING: 60, // Section headings (likely protected content)
  PARAGRAPH: 61, // Body text (likely protected content)
  CAPTION: 62, // Smallest text
};

/**
 * Loads and accumulates event fonts while coordinating the matching layout theme.
 * `isThemeCurrent` is true only when `theme` was computed from the exact current
 * `eventDesign` reference, allowing consumers to avoid mixed-design transitions.
 */
export const useEventFontsLoader = (
  eventDesign: Maybe<EventDesignFragment>,
  options?: { useSelfHostedFonts?: boolean; mirroredFamilies?: ReadonlyArray<string>; shouldLoadHeaderTitleFonts?: boolean },
) => {
  const [loadedFonts, { addMultiple: addFonts, has: hasFont, reset: clearLoadedFonts }] = useSet<string>();
  const [loadingFonts, setLoadingFonts] = useState(false);

  // Timeout utilities for font loading delays
  const initialLoadTimeout = useTimeout();
  const fontPaintTimeout = useTimeout();
  const fontFallbackTimeout = useTimeout();

  // When self-hosted is enabled, check if a family has been mirrored.
  // If no mirroredFamilies list is provided (missing payload, misconfigured flag),
  // default to "not mirrored" and fall back to Google — this is the safe default
  // since an optimistic assumption would cause 404s for unmirrored families.
  const mirroredSet = useMemo(() => (options?.mirroredFamilies ? new Set(options.mirroredFamilies) : null), [options?.mirroredFamilies]);
  const isMirrored = useCallback(
    (family: string) => {
      if (!mirroredSet) return false;
      // Normalize legacy aliases (e.g., "SirinStencil" → "Sirin Stencil") before lookup,
      // since the mirrored catalog uses normalized names from the mirror script.
      return mirroredSet.has(normalizeFontFamily(family));
    },
    [mirroredSet],
  );

  // Track initial load vs dynamic changes
  const isInitialLoadRef = useRef(true);

  // Theme computation
  const layoutConfig = eventDesign?.websiteLayout?.layoutType === 'brannan' ? layoutConfigs.brannan : layoutConfigs.aloha;

  // Keep the computed theme and its source reference in one state update so consumers can
  // distinguish a current base theme from the brief design/theme skew during font transitions.
  const [themeState, setThemeState] = useState<Readonly<{ theme: Theme; eventDesign: Maybe<EventDesignFragment> }>>(() => ({
    theme: eventDesign ? layoutConfig.configureThemeOverrides(eventDesign, layoutConfig.theme) : layoutConfig.theme,
    eventDesign,
  }));

  /** Atomically publishes a theme with the exact event design reference used to compute it. */
  const updateTheme = useCallback(() => {
    setThemeState({
      theme: eventDesign ? layoutConfig.configureThemeOverrides(eventDesign, layoutConfig.theme) : layoutConfig.theme,
      eventDesign,
    });
  }, [eventDesign, layoutConfig]);

  const { theme } = themeState;
  const isThemeCurrent = themeState.eventDesign === eventDesign;

  /**
   * Track accumulated fonts for Helmet (with LRU cache limit)
   *
   * CRITICAL: Font accumulation prevents flickering during font changes
   * - Without accumulation: Helmet removes old <link> tags when eventDesign changes
   * - Old fonts become unavailable → browser falls back to Times/default serif
   * - User sees text flicker from "Old Font" → "Times" → "New Font"
   *
   * With accumulation: Helmet keeps previously selected fonts in <head>
   * - Old fonts remain available during transition
   * - Text stays in "Old Font" while "New Font" loads → smooth transition to "New Font"
   * - No visible flicker to default fonts
   *
   * LRU CACHE (limit: 20 fonts):
   * - When cache reaches 20 fonts, it's cleared and rebuilt with current fonts
   * - Clearing causes brief flicker but prevents unbounded memory growth
   * - Trade-off: Smooth UX for normal usage, memory control for extreme cases
   *
   * MEMORY IMPACT: Each cached font ~50-200KB
   * - 20 fonts = ~1-4MB (acceptable for designer sessions)
   * - Cache clears when limit reached (prevents runaway memory)
   */
  const accumulatedFontsRef = useRef<Map<string, { family: string; includeItalic: boolean; priority: number }>>(new Map());

  // Collect all fonts to preload
  const fontsToLoad = useMemo(() => {
    // Use Map to properly deduplicate by font family (Sets use reference equality for objects)
    const fontMap = new Map<string, { family: string; includeItalic: boolean; priority: number }>();

    if (eventDesign) {
      // Font pack fonts FIRST (highest priority - used for hero content)
      if (eventDesign.fonts) {
        eventDesign.fonts.forEach(fontEntry => {
          if (fontEntry.font?.fontFamily) {
            // Use UNCATEGORIZED_FONT_PRIORITY for fonts without explicit category mapping.
            // Explicit prioritization via CATEGORY_PRIORITY_MAP optimizes for unauthenticated user experience.
            const priority = CATEGORY_PRIORITY_MAP[fontEntry.category] ?? UNCATEGORIZED_FONT_PRIORITY;
            // Font pack fonts need italic variants
            const existing = fontMap.get(fontEntry.font.fontFamily);
            if (!existing || existing.priority > priority) {
              fontMap.set(fontEntry.font.fontFamily, { family: fontEntry.font.fontFamily, includeItalic: true, priority });
            }
          }
        });
      }

      // Per-line fonts of the header's own rich event title (eventName.contentHtml — photo arch
      // today, more layouts later). They live in the header dataJSON, not the font pack, but
      // style spans of the hero h1, so they are as LCP-critical as the EVENT_TITLE pack font.
      // Callers gate this on the multiline-title flag so flag-off guests don't download fonts
      // the title render won't use.
      const titleHtml = options?.shouldLoadHeaderTitleFonts ? getHeaderEventTitleHtml(eventDesign.activeWebsiteHeaderPresentationLayout?.dataJSON) : undefined;
      if (titleHtml) {
        extractFontFamiliesFromHtml(titleHtml).forEach(family => {
          if (!fontMap.has(family)) {
            fontMap.set(family, { family, includeItalic: false, priority: CATEGORY_PRIORITY_MAP.EVENT_TITLE });
          }
        });
      }

      // Primary event font (medium priority)
      if (eventDesign.font?.fontFamily) {
        if (!fontMap.has(eventDesign.font.fontFamily)) {
          fontMap.set(eventDesign.font.fontFamily, { family: eventDesign.font.fontFamily, includeItalic: false, priority: 50 });
        }
      }

      // Layout fonts LAST (lowest priority - decorative)
      const layoutType = eventDesign.websiteLayout?.layoutType;
      const layoutConfig = layoutType === 'brannan' ? layoutConfigs.brannan : layoutConfigs.aloha;
      layoutConfig.fonts.google?.forEach(fontKey => {
        const family = googleFontFamilies[fontKey];
        if (!fontMap.has(family)) {
          fontMap.set(family, { family, includeItalic: false, priority: 100 });
        }
      });
    }

    // LRU cache: limit accumulated fonts to prevent unbounded memory growth
    // When limit reached, clear cache and start fresh (will cause brief flicker, but keeps memory under control)
    if (accumulatedFontsRef.current.size >= MAX_ACCUMULATED_FONTS) {
      accumulatedFontsRef.current.clear();
      clearLoadedFonts();
    }

    // Accumulate fonts: merge current fonts with previously seen fonts
    // This ensures Helmet never removes old font stylesheets during dynamic changes
    fontMap.forEach((value, key) => {
      const existing = accumulatedFontsRef.current.get(key);
      if (!existing || existing.priority > value.priority) {
        accumulatedFontsRef.current.set(key, value);
      }
    });

    // Sort by priority (lowest number = highest priority)
    const fontsArray = Array.from(accumulatedFontsRef.current.values()).sort((a, b) => a.priority - b.priority);
    return fontsArray;
  }, [eventDesign, clearLoadedFonts, options?.shouldLoadHeaderTitleFonts]);

  /**
   * Detect newly added fonts (not yet loaded)
   *
   * CRITICAL: Filter based on loadedFonts Set, not a "previous fonts" ref
   * - loadedFonts accumulates ALL fonts loaded during the session
   * - Prevents re-loading fonts when user switches back to a previous selection
   * - Without this: switching A→B→A would trigger WebFont.js to load A again
   * - WebFont.js marks already-loaded fonts as "inactive" → timeout/flicker
   */
  const newFonts = useMemo(() => {
    const currentFontFamilies = fontsToLoad.map(f => f.family);
    const newlyAdded = currentFontFamilies.filter(font => !loadedFonts.has(font));
    return newlyAdded;
  }, [fontsToLoad, loadedFonts]);

  // Initial load: Mark fonts as loaded quickly (preloading via Helmet)
  useEffect(() => {
    // Treat as initial load if: (1) first mount, or (2) HMR cleared loadedFonts but we have fonts to load
    const shouldTreatAsInitialLoad = isInitialLoadRef.current || (fontsToLoad.length > 0 && loadedFonts.size === 0);

    if (shouldTreatAsInitialLoad && fontsToLoad.length > 0) {
      const fontFamilies = fontsToLoad.map(f => f.family);

      initialLoadTimeout.set(() => {
        addFonts(fontFamilies);
        isInitialLoadRef.current = false;
        updateTheme();
      }, FONT_PRELOAD_DISCOVERY_DELAY_MS);
      return;
    }

    // If all fonts already loaded on initial render
    if (shouldTreatAsInitialLoad && fontsToLoad.length === 0) {
      updateTheme();
      isInitialLoadRef.current = false;
    }
  }, [fontsToLoad, addFonts, loadedFonts.size, updateTheme, initialLoadTimeout]);

  /**
   * Dynamic changes: Wait for WebFont.js (prevents FOUT in designer)
   *
   * Specifies bold/italic/bolditalic weights to match Helmet's CSS URLs.
   * - Wedding websites require proper bold rendering (synthetic bold is too light)
   * - WebFont.js format: "FontFamily:bold,italic,bolditalic" maps to weights 400,700 + italic variants
   * - For fonts with limited weights (e.g., Alex Brush), WebFont.js only loads available variants
   * - No timeout issues when combined with font accumulation + correct newFonts detection
   */
  useEffect(() => {
    if (!isInitialLoadRef.current) {
      // No new fonts - just update theme immediately (e.g., eventDesign changed but fonts same)
      if (newFonts.length === 0) {
        updateTheme();
        return;
      }

      // New fonts detected - load via WebFont.js
      if (globalWindow.WebFont) {
        setLoadingFonts(true);

        // Split fonts into mirrored (Joy CDN via custom provider) and unmirrored (Google fallback).
        // WebFont.js supports both providers in a single load() call, so mixed loading works seamlessly.
        const mirroredFonts = options?.useSelfHostedFonts ? newFonts.filter(f => isMirrored(f)) : [];
        const unmirroredFonts = options?.useSelfHostedFonts ? newFonts.filter(f => !isMirrored(f)) : newFonts;

        const webFontConfig: WebFont.Config = {};
        if (mirroredFonts.length > 0) {
          // "custom" provider: loads fonts from the specified CSS URLs (Joy CDN index.css per family).
          // Use bare family names — the custom provider expects FVD notation (e.g., "n4,n7"),
          // NOT the Google-style "bold,italic,bolditalic" format. Bare names test the default
          // variation (normal 400) which is sufficient for detecting font availability.
          // Normalize family names so they match the font-family declared in the self-hosted CSS.
          // e.g., event data may have "SirinStencil" but the CSS declares "Sirin Stencil".
          const normalizedFamilies = mirroredFonts.map(normalizeFontFamily);
          webFontConfig.custom = {
            families: normalizedFamilies,
            urls: mirroredFonts.map(font => buildSelfHostedFontCssUrl(font)),
          };
        }
        if (unmirroredFonts.length > 0) {
          // "google" provider uses its own family format with weight descriptors
          webFontConfig.google = {
            families: unmirroredFonts.map(font => `${font}:bold,italic,bolditalic`),
          };
        }

        globalWindow.WebFont.load({
          ...webFontConfig,
          active: () => {
            fontPaintTimeout.set(() => {
              updateTheme();
              addFonts(newFonts);
              setLoadingFonts(false);
            }, FONT_PAINT_DELAY_MS);
          },
          inactive: () => {
            // Alert to New Relic when self-hosted fonts fail to load (404, network error, CDN issue).
            // This is distinct from unmirrored fonts which silently fall back to Google.
            if (options?.useSelfHostedFonts && mirroredFonts.length > 0) {
              addError(`Self-hosted font load failed: ${mirroredFonts.join(', ')}`, { category: 'self-hosted-fonts-load-failure', fontFamilies: mirroredFonts.join(', ') });
            }
            fontFallbackTimeout.set(() => {
              addFonts(newFonts);
              setLoadingFonts(false);
              updateTheme();
            }, FONT_LOAD_TIMEOUT_FALLBACK_MS);
          },
        });
      }
    }
  }, [newFonts, addFonts, updateTheme, fontPaintTimeout, fontFallbackTimeout, options?.useSelfHostedFonts, isMirrored]);

  // Generate Helmet <link> tags for preloading
  // fontsToLoad accumulates fonts (never removes), so Helmet adds new fonts without removing old ones
  const fontLoadLinks = useMemo<FontPreloadLinkAttributes>(() => {
    // Self-hosted path: emit Joy CDN stylesheet for all mirrored families, plus direct .woff2
    // preloads only for fonts that are already loaded (i.e., from the initial SSR render).
    //
    // Preloads are NOT emitted for dynamically-added fonts (font pack changes in the designer).
    // Reason: preloads make the font available almost instantly, which causes font-display:swap
    // to render text before WebFont.js coordinates the theme update — producing a brief flicker.
    // For dynamic changes, WebFont.js loads the font via the CSS stylesheet and fires the
    // active callback when ready, at which point the theme updates atomically (no flicker).
    //
    // Unmirrored families silently fall back to Google CSS URLs.
    if (options?.useSelfHostedFonts) {
      return fontsToLoad.flatMap(({ family, includeItalic }) => {
        if (!isMirrored(family)) {
          const url = buildGoogleFontUrl(family, { includeItalic });
          return [
            { key: `preload-${family}`, rel: 'preload', as: 'style', href: url },
            { key: `stylesheet-${family}`, rel: 'stylesheet', href: url },
          ];
        }

        // URL builders normalize aliases internally (e.g., "SirinStencil" → "sirin-stencil").
        const cssUrl = buildSelfHostedFontCssUrl(family);
        const links: Array<FontPreloadLinkAttributes[number]> = [{ key: `stylesheet-${family}`, rel: 'stylesheet', href: cssUrl }];

        // Only preload binaries for fonts from the initial render (already in loadedFonts).
        // Dynamically-added fonts rely on WebFont.js for coordinated loading.
        const isInitialFont = loadedFonts.has(family) || isInitialLoadRef.current;
        if (isInitialFont) {
          const preloadUrls = buildCriticalPreloadUrls(family);
          preloadUrls.forEach((url, i) => {
            links.push({ key: `preload-font-${family}-${i}`, rel: 'preload', as: 'font', href: url, type: 'font/woff2', crossOrigin: 'anonymous' });
          });
        }

        return links;
      });
    }

    return fontsToLoad.flatMap(({ family, includeItalic }) => {
      const url = buildGoogleFontUrl(family, { includeItalic });
      return [
        { key: `preload-${family}`, rel: 'preload', as: 'style', href: url },
        { key: `stylesheet-${family}`, rel: 'stylesheet', href: url },
      ];
    });
  }, [fontsToLoad, options?.useSelfHostedFonts, isMirrored, loadedFonts]);

  return { fontLoadLinks, loadedFonts, addFonts, hasFont, theme, isThemeCurrent, loadingFonts };
};
